/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { SortableFields } from 'embercom/lib/suggestions/constants';
import AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import Service, { inject as service } from '@ember/service';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { tracked } from '@glimmer/tracking';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { EntityType } from 'embercom/models/data/entity-types';
import { REVIEWABLE_CONTENT_ENTITIES } from 'embercom/lib/ai-content-library/constants';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { getOwner } from '@ember/application';
import { type ProjectedConversationsRanking } from 'embercom/lib/suggestions/constants';
import type IntlService from 'embercom/services/intl';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { fetchTaskSuggestions } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/task-suggestions/task-suggestions-data-resources';
import type Store from '@ember-data/store';
import type ReportingChartService from './reporting-chart-service';
import type { TaskSuggestionStatus } from 'embercom/models/content-service/task-suggestion';
import {
  applyFilters,
  type OptimizeSuggestionFilterState,
} from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/optimize-suggestion-filter-helper';
import moment from 'moment-timezone';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

export type OptimizeSuggestionItem = {
  entityId: string;
  contentType?: EntityType;
  suggestionType: 'content-gap' | 'data-gap' | 'action-gap';
  suggestionTypeDisplay: string;
  title: string;
  description: string;
  createdAt: string;
  conversationIds: string[];
  suggestionState: Status | TaskSuggestionStatus;
  topicList: string[];
  subtopicList: string[];
  ranking: ProjectedConversationsRanking;
  suggestion: ContentWrapper | TaskSuggestion;
  icon: InterfaceIconName;
};

export type OptimizeSuggestionList = OptimizeSuggestionItem[];

export default class OptimizeSuggestionsService extends Service {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare reportingChartService: ReportingChartService;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked declare fullSuggestions: OptimizeSuggestionList | null[];
  @tracked declare filterState?: OptimizeSuggestionFilterState;

  @restartableTask
  *loadSuggestions(): TaskGenerator<void> {
    let promises: Promise<any>[] = [];
    promises.push(this.contentAPI());
    promises.push(this.loadTaskSuggestions());
    let [api, taskSuggestionsResponse] = yield Promise.all(promises);
    let contentSuggestions: OptimizeSuggestionList | null[] = [];
    let taskSuggestions: OptimizeSuggestionList | null[] = [];
    if (!api.isLoadingPage) {
      contentSuggestions = this.compileContentSuggestions(api.contentWrappers) as
        | OptimizeSuggestionList
        | null[];
    }
    if (taskSuggestionsResponse.length > 0) {
      taskSuggestions = this.compileTaskSuggestions(taskSuggestionsResponse) as
        | OptimizeSuggestionList
        | null[];
    }
    this.fullSuggestions = [...contentSuggestions, ...taskSuggestions] as OptimizeSuggestionList;
  }

  get suggestions(): OptimizeSuggestionList {
    if (!this.filterState) {
      let firstDate = this.getSuggestionGenerationDates()[0]?.timestamp;
      if (firstDate) {
        this.setFilterState({
          suggestionType: [],
          date: firstDate,
          topics: [],
        });
      } else {
        this.setFilterState({
          suggestionType: [],
          date: undefined,
          topics: [],
        });
      }
    }
    return applyFilters(
      this.appService,
      this.fullSuggestions as OptimizeSuggestionList,
      this.filterState as OptimizeSuggestionFilterState,
    );
  }

  setFilterState(filterState: OptimizeSuggestionFilterState) {
    this.filterState = filterState;
  }

  findSuggestionByEntityId(entityId: string) {
    return this.fullSuggestions.find((suggestion) => suggestion?.entityId === entityId);
  }

  predictedSuggestionsCount(filterState: OptimizeSuggestionFilterState): number {
    let suggestionCopy = this.fullSuggestions.slice();
    return applyFilters(this.appService, suggestionCopy as OptimizeSuggestionList, filterState)
      .length;
  }

  getSuggestionGenerationDates(): Array<{
    timestamp: string | undefined;
    formattedTimestamp: string;
    label: string;
  }> {
    let timestamps = this.fullSuggestions
      .map((suggestion) => suggestion?.createdAt)
      .filter((date): date is string => date !== null && date !== undefined);

    if (!timestamps || timestamps.length === 0) {
      return [];
    }

    // Group by unique date (day only, ignoring time) and keep one timestamp per date
    let dateMap = new Map<string, string>();
    timestamps.forEach((timestamp) => {
      let momentDate = moment.tz(timestamp, this.appService.app.timezone);
      let dateKey = momentDate.format('YYYY-MM-DD');

      // Keep the first timestamp we encounter for each date
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, timestamp);
      }
    });

    // Convert map to array and format labels
    return Array.from(dateMap.values())
      .sort((a, b) => {
        return (
          moment.tz(b, this.appService.app.timezone).valueOf() -
          moment.tz(a, this.appService.app.timezone).valueOf()
        );
      })
      .map((timestamp) => {
        let startOfWeek = moment
          .tz(timestamp, this.appService.app.timezone)
          .startOf('week')
          .startOf('day');
        let endOfWeek = moment
          .tz(timestamp, this.appService.app.timezone)
          .endOf('week')
          .endOf('day');

        return {
          timestamp,
          formattedTimestamp: moment
            .tz(timestamp, this.appService.app.timezone)
            .format('YYYY-MM-DD'),
          label: `${startOfWeek.format('MMM D, YYYY')} - ${endOfWeek.format('MMM D, YYYY')}`,
        };
      });
  }

  getFirstTimestampForType(type: 'content-gap' | 'data-gap' | 'action-gap'): string {
    let existingDates = this.getSuggestionGenerationDates();
    let formattedExistingDates = existingDates.map((date) => date.formattedTimestamp);

    let firstSuggestion = this.fullSuggestions.find((suggestion) => {
      let suggestionDate = moment.tz(suggestion?.createdAt, this.appService.app.timezone);

      return (
        suggestion?.suggestionType === type &&
        formattedExistingDates.includes(suggestionDate.format('YYYY-MM-DD'))
      );
    });

    let formattedDate = moment
      .tz(firstSuggestion?.createdAt, this.appService.app.timezone)
      .format('YYYY-MM-DD');
    return existingDates.find((date) => date.formattedTimestamp === formattedDate)
      ?.timestamp as string;
  }

  getRelevantTopics(): string[] {
    return this.fullSuggestions
      .map((suggestion) => suggestion?.topicList)
      .flat()
      .uniq()
      .compact()
      .sort();
  }

  private async contentAPI() {
    let sort_by = SortableFields.Priority;

    let api = new AiContentLibraryApi(getOwner(this), {
      object_types: this.reviewableContentEntities(),
      content_wrapper_types: [ContentWrapperType.AI_CONTENT],
      app_id: this.appService.app.id as string,
      per_page: 100,
      content_review_statuses: [Status.Pending],
      sort_by,
      sort_direction: 'desc',
    });

    let promises: Promise<any>[] = [api.loadPage()];
    promises.push(this.knowledgeHubService.fetchAvailableLocalesFromMessengerAndHelpCenter());
    await Promise.all(promises);

    this.contentSuggestionsService.listApi = api;

    return api;
  }

  private async loadTaskSuggestions() {
    let taskSuggestions = await fetchTaskSuggestions(
      this.store,
      this.appService.app.id,
      this.reportingChartService.syntheticDataUseCase,
    );
    return taskSuggestions;
  }

  private compileTaskSuggestions(taskSuggestions: TaskSuggestion[]) {
    return taskSuggestions.map((taskSuggestion: TaskSuggestion) => {
      let id = taskSuggestion.id || Math.floor(Math.random() * 1000000).toString();
      let createdAt: string;
      if (taskSuggestion.createdAt) {
        if (typeof taskSuggestion.createdAt === 'string') {
          createdAt = taskSuggestion.createdAt;
        } else {
          createdAt = taskSuggestion.createdAt.toISOString();
        }
      } else {
        createdAt = new Date().toISOString();
      }
      let suggestionType = this.taskSuggestionType(taskSuggestion);
      let icon = suggestionType === 'data-gap' ? 'recipient-data' : 'webhook';
      let suggestionTypeDisplay = this.intl.t(
        `components.reporting.automation.optimize-suggestions.filter.reason.${suggestionType}`,
      );

      return {
        entityId: id,
        contentType: undefined,
        suggestionType,
        suggestionTypeDisplay,
        title: taskSuggestion.actionName,
        description: taskSuggestion.actionSummary,
        createdAt,
        conversationIds: taskSuggestion.sampledConversationIds,
        suggestionState: taskSuggestion.status,
        topicList: taskSuggestion.topicNameList,
        subtopicList: taskSuggestion.subtopics,
        ranking: 1, // these dont have a ranking
        suggestion: taskSuggestion,
        icon,
      };
    });
  }

  private compileContentSuggestions(contentWrappers: ContentWrapper[]) {
    return contentWrappers.map((contentWrapper: ContentWrapper) => {
      let content = contentWrapper.contents.firstObject;
      if (!content || !contentWrapper.entityId) {
        return null;
      }

      let suggestionTypeDisplay = this.intl.t(
        `components.reporting.automation.optimize-suggestions.filter.reason.content-gap`,
      );

      return {
        entityId: contentWrapper.entityId.toString(),
        contentType: content.contentType,
        suggestionType: 'content-gap',
        suggestionTypeDisplay,
        title: this.contentTitle(content.contentReviewType, content.contentType),
        description: contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation,
        createdAt: content.contentData.most_recent_content_review_request_created_at,
        conversationIds: contentWrapper.contentData.suggestion_metadata.included_conversations,
        suggestionState: content.contentData.status,
        topicList: [content.contentData.suggestion_metadata.topic],
        subtopicList: content.contentData.suggestion_metadata.subtopics,
        ranking: content.contentData.projected_conversations_ranking,
        suggestion: contentWrapper,
        icon: 'knowledge',
      };
    });
  }

  private taskSuggestionType(taskSuggestion: TaskSuggestion) {
    return taskSuggestion.isDataGap ? 'data-gap' : 'action-gap';
  }

  private contentTitle(contentReviewType: ReviewType, contentType: EntityType) {
    let objectKey = contentType === EntityType.ContentSnippet ? 'snippet' : 'article';
    switch (contentReviewType) {
      case ReviewType.CREATE_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.add-content');
      case ReviewType.EDIT_CONTENT:
        //next 2 can also be snippet but i need to figure out how to handle that
        return this.intl.t(`ai-agent.suggestions.list-item.titles.edit-${objectKey}`);
      case ReviewType.DELETE_CONTENT:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.delete-${objectKey}`);
      case ReviewType.DUPLICATE_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.duplicate-content');
      case ReviewType.CONTRADICTING_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.contradicting-content');
      case ReviewType.UNDERPERFORMING_CONTENT:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.underperforming-${objectKey}`);
      default:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.fallback-${objectKey}`);
    }
  }

  private reviewableContentEntities(): EntityType[] {
    let reviewableContentEntities = [...REVIEWABLE_CONTENT_ENTITIES];
    reviewableContentEntities.push(EntityType.ArticleContent);
    reviewableContentEntities.push(EntityType.ContentSectionRelationship);
    return reviewableContentEntities;
  }
}

declare module '@ember/service' {
  interface Registry {
    optimizeSuggestionsService: OptimizeSuggestionsService;
    'optimize-suggestions-service': OptimizeSuggestionsService;
  }
}
